import { FC, useCallback, useEffect, useRef, useState } from 'react';

import { ClockIcon, PhoneIcon, ShieldCheckIcon } from '@heroicons/react/outline';
import {
  confirmVerificationCode,
  sendVerificationCode,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import { AxiosError } from 'axios';

import {
  RESTRICTED_PHONE_ERROR_MESSAGE,
  formatPhoneNumber,
  isPhoneAllowedByRestrictedPhone,
} from './giftCouponRedeem.utils';

interface PhoneVerificationSectionProps {
  couponId: string;
  isSelectable: boolean;
  phoneNumber: string;
  onPhoneNumberChange: (value: string) => void;
  isVerified: boolean;
  onVerified: () => void;
  onVerificationReset: () => void;
  submitError: string | null;
  isSubmitting: boolean;
  onSubmit: () => void;
  isPreset?: boolean;
  verifiedAt?: Date;
  hideSubmitButton?: boolean;
  requesterName?: string;
  onRequesterNameChange?: (value: string) => void;
  showRequesterName?: boolean;
  titleText?: string;
  descriptionText?: string;
  subText?: string;
  /**
   * 회수 잠금 / 재발행 쿠폰의 사전 등록 수령자 번호 (정규식). 지정되어 있으면
   * 입력된 번호가 이 패턴에 매칭되어야만 본인 인증 발송이 가능하다.
   */
  additionalRecipientPhone?: string;
  /** 추가교환(extra) 신청 모드 — 인증을 원본 쿠폰에 덮어쓰지 않도록 백엔드에 신호 */
  isExtra?: boolean;
}

const PhoneVerificationSection: FC<PhoneVerificationSectionProps> = ({
  couponId,
  isSelectable,
  phoneNumber,
  onPhoneNumberChange,
  isVerified,
  onVerified,
  onVerificationReset,
  submitError,
  isSubmitting,
  onSubmit,
  isPreset,
  verifiedAt,
  hideSubmitButton,
  requesterName,
  onRequesterNameChange,
  showRequesterName,
  titleText = '받으실 번호를 입력해 주세요',
  descriptionText = '답례품을 받으실 휴대폰 번호를 입력해 주세요',
  subText,
  additionalRecipientPhone,
  isExtra,
}) => {
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isConfirmingCode, setIsConfirmingCode] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [expirySeconds, setExpirySeconds] = useState(0);
  const cooldownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const expiryTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
      if (expiryTimerRef.current) clearInterval(expiryTimerRef.current);
    };
  }, []);

  const startCooldownTimer = useCallback((seconds: number) => {
    setCooldownSeconds(seconds);
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
    cooldownTimerRef.current = setInterval(() => {
      setCooldownSeconds((prev) => {
        if (prev <= 1) {
          if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const startExpiryTimer = useCallback((expiresAt: string) => {
    const remaining = Math.max(
      0,
      Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000),
    );
    setExpirySeconds(remaining);
    if (expiryTimerRef.current) clearInterval(expiryTimerRef.current);
    expiryTimerRef.current = setInterval(() => {
      setExpirySeconds((prev) => {
        if (prev <= 1) {
          if (expiryTimerRef.current) clearInterval(expiryTimerRef.current);
          setVerificationId(null);
          setVerificationCode('');
          setVerificationError('인증시간이 만료되었습니다. 다시 인증번호를 받아주세요.');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatPhoneNumber(e.target.value);
      onPhoneNumberChange(formatted);
      if (isVerified) {
        onVerificationReset();
        setVerificationId(null);
        setVerificationCode('');
        setVerificationError(null);
      }
    },
    [isVerified, onPhoneNumberChange, onVerificationReset],
  );

  const handleResetPhoneInput = useCallback(() => {
    setVerificationId(null);
    setVerificationCode('');
    setVerificationError(null);
    setExpirySeconds(0);
    setCooldownSeconds(0);
    if (expiryTimerRef.current) clearInterval(expiryTimerRef.current);
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
  }, []);

  const handleSendVerification = useCallback(async () => {
    const cleanPhone = phoneNumber.replace(/-/g, '');
    if (!/^01[016789]\d{8}$/.test(cleanPhone)) {
      setVerificationError('올바른 휴대폰 번호를 입력해 주세요.');
      return;
    }

    if (!isPhoneAllowedByRestrictedPhone(cleanPhone, additionalRecipientPhone)) {
      setVerificationError(RESTRICTED_PHONE_ERROR_MESSAGE);
      return;
    }

    setIsSendingCode(true);
    setVerificationError(null);

    try {
      const result = await sendVerificationCode({
        phoneNumber: cleanPhone,
        giftCouponId: couponId,
        ...(isExtra ? { isExtra: true } : {}),
      });
      setVerificationId(result.verificationId);
      setVerificationCode('');
      startCooldownTimer(30);
      startExpiryTimer(result.expiresAt);
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>;
      const msg = error.response?.data?.message;
      if (error.response?.status === 400) {
        setVerificationError(msg || '인증번호 발송에 실패했습니다.');
      } else {
        setVerificationError('인증번호 발송에 실패했습니다. 다시 시도해 주세요.');
      }
    } finally {
      setIsSendingCode(false);
    }
  }, [
    phoneNumber,
    couponId,
    additionalRecipientPhone,
    isExtra,
    startCooldownTimer,
    startExpiryTimer,
  ]);

  const handleConfirmVerification = useCallback(async () => {
    if (!verificationId) return;

    if (verificationCode.length !== 6) {
      setVerificationError('6자리 인증코드를 입력해 주세요.');
      return;
    }

    setIsConfirmingCode(true);
    setVerificationError(null);

    try {
      await confirmVerificationCode({
        verificationId,
        code: verificationCode,
        giftCouponId: couponId,
        phoneNumber: phoneNumber.replace(/-/g, ''),
        ...(isExtra ? { isExtra: true } : {}),
      });
      onVerified();
      if (expiryTimerRef.current) clearInterval(expiryTimerRef.current);
      setExpirySeconds(0);
    } catch (err) {
      const error = err as AxiosError<{ message?: string }>;
      const status = error.response?.status;
      const msg = error.response?.data?.message;
      if (status === 410) {
        setVerificationError(msg || '인증이 만료되었습니다. 다시 인증번호를 받아주세요.');
        setVerificationId(null);
        setVerificationCode('');
        if (expiryTimerRef.current) clearInterval(expiryTimerRef.current);
        setExpirySeconds(0);
      } else if (status === 400) {
        setVerificationError(msg || '인증코드가 일치하지 않습니다.');
      } else if (status === 404) {
        setVerificationError('쿠폰을 찾을 수 없습니다.');
      } else {
        setVerificationError('인증 확인에 실패했습니다. 다시 시도해 주세요.');
      }
    } finally {
      setIsConfirmingCode(false);
    }
  }, [verificationId, verificationCode, couponId, phoneNumber, isExtra, onVerified]);

  return (
    <div className="mt-6 rounded-xl border border-stone-200 bg-white p-5">
      {isSelectable && (
        <div className="mb-4 flex items-center justify-center gap-2">
          <span className="rounded-full bg-stone-800 px-2.5 py-0.5 text-base font-bold text-white">
            STEP 2
          </span>
        </div>
      )}

      <h3 className="text-center text-lg font-bold text-stone-700">{titleText}</h3>
      <p className="mt-1.5 text-center text-sm text-stone-400">{descriptionText}</p>
      {subText && <p className="mt-1 text-center text-xs text-stone-300">{subText}</p>}

      {/* 본인 성함 입력 */}
      {showRequesterName && (
        <div className="mt-4">
          <label className="mb-1.5 block text-sm font-medium text-stone-600">
            본인 성함
          </label>
          <input
            type="text"
            value={requesterName || ''}
            onChange={(e) => onRequesterNameChange?.(e.target.value)}
            placeholder="본인 성함을 입력해 주세요"
            disabled={isVerified}
            className={`w-full rounded-lg border px-4 py-3.5 text-base text-stone-700 placeholder:text-stone-300 focus:outline-none ${
              isVerified
                ? 'border-green-300 bg-green-50 text-green-700'
                : 'border-stone-200 focus:border-stone-400'
            }`}
          />
        </div>
      )}

      {/* 번호 입력 */}
      <div className="mt-4">
        <div className="relative">
          <PhoneIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-stone-300" />
          <input
            type="tel"
            value={phoneNumber}
            onChange={handlePhoneChange}
            placeholder="010-1234-5678"
            maxLength={13}
            disabled={isVerified || !!verificationId}
            className={`w-full rounded-lg border py-4 pl-12 pr-4 text-center text-xl font-medium tracking-wider focus:outline-none ${
              isVerified
                ? 'border-green-300 bg-green-50 text-green-700'
                : verificationId
                ? 'border-stone-300 bg-stone-50 text-stone-500'
                : 'border-stone-200 text-stone-700 focus:border-stone-400'
            }`}
          />
        </div>

        {isVerified && (
          <div className="mt-2">
            <div className="flex items-center justify-center gap-1.5 text-green-600">
              <ShieldCheckIcon className="h-4 w-4" />
              <span className="text-sm font-medium">본인 인증 완료</span>
            </div>
            {isPreset && (
              <div className="mt-1.5 text-center">
                <p className="text-xs text-stone-400">
                  이전에 인증하신 번호입니다
                  {verifiedAt && (
                    <span className="ml-1 text-stone-300">
                      (
                      {new Date(verifiedAt).toLocaleDateString('ko-KR', {
                        month: 'numeric',
                        day: 'numeric',
                      })}{' '}
                      인증)
                    </span>
                  )}
                </p>
                <button
                  onClick={() => {
                    onPhoneNumberChange('');
                    onVerificationReset();
                    setVerificationId(null);
                    setVerificationCode('');
                    setVerificationError(null);
                  }}
                  className="mt-1 text-xs font-medium text-stone-500 underline underline-offset-2"
                >
                  다른 번호로 인증하기
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 인증번호 받기 버튼 (발송 전) */}
      {!verificationId && !isVerified && (
        <button
          onClick={() => {
            if (isSendingCode) return;
            if (phoneNumber.replace(/-/g, '').length < 10) {
              setVerificationError('올바른 휴대폰 번호 양식을 입력해 주세요.');
              return;
            }
            handleSendVerification();
          }}
          className={`mt-4 flex w-full items-center justify-center rounded-lg py-3.5 text-sm font-bold transition ${
            isSendingCode || phoneNumber.replace(/-/g, '').length < 10
              ? 'bg-stone-100 text-stone-400'
              : 'border border-stone-300 bg-white text-stone-700 active:bg-stone-50'
          }`}
        >
          {isSendingCode ? '발송중...' : '본인 인증하기'}
        </button>
      )}

      {/* 인증코드 입력 (발송 후 & 인증 전) */}
      {verificationId && !isVerified && (
        <div className="mt-4">
          <div className="relative flex gap-2">
            <div className="relative flex-1">
              <ShieldCheckIcon className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-stone-300" />
              <input
                type="text"
                inputMode="numeric"
                value={verificationCode}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9]/g, '').slice(0, 6);
                  setVerificationCode(v);
                  setVerificationError(null);
                }}
                placeholder="인증번호 6자리"
                maxLength={6}
                className={`w-full rounded-lg border border-stone-200 py-4 pl-12 pr-4 text-center text-xl font-medium text-stone-700 placeholder:text-base placeholder:font-normal placeholder:tracking-normal placeholder:text-stone-300 focus:border-stone-400 focus:outline-none ${
                  verificationCode ? 'tracking-[0.3em]' : 'tracking-normal'
                }`}
              />
            </div>
            <button
              onClick={handleConfirmVerification}
              disabled={isConfirmingCode || verificationCode.length !== 6}
              className={`flex-shrink-0 rounded-lg px-4 py-4 text-sm font-bold transition ${
                isConfirmingCode || verificationCode.length !== 6
                  ? 'bg-stone-100 text-stone-400'
                  : 'bg-stone-800 text-white active:bg-stone-900'
              }`}
            >
              {isConfirmingCode ? '확인중...' : '확인'}
            </button>
          </div>

          {expirySeconds > 0 && (
            <div className="mt-2 flex items-center justify-center gap-1.5">
              <ClockIcon className="h-3.5 w-3.5 text-stone-400" />
              <span className="text-sm text-stone-400">
                유효시간{' '}
                <span className="font-medium text-stone-600">
                  {Math.floor(expirySeconds / 60)}:
                  {String(expirySeconds % 60).padStart(2, '0')}
                </span>
              </span>
            </div>
          )}

          <div className="mt-3 flex items-center justify-center gap-3">
            <div>
              <span className="text-xs text-stone-400">인증번호가 안 오셨나요? </span>
              <button
                onClick={() => {
                  if (cooldownSeconds > 0) {
                    setVerificationError(
                      `${cooldownSeconds}초 후에 재발송할 수 있습니다.`,
                    );
                    return;
                  }
                  handleSendVerification();
                }}
                disabled={isSendingCode}
                className={`text-xs font-semibold ${
                  isSendingCode ? 'text-stone-300' : 'text-stone-600 underline'
                }`}
              >
                {isSendingCode ? '발송중...' : '재발송'}
              </button>
            </div>
            <span className="text-xs text-stone-200">|</span>
            <button
              onClick={handleResetPhoneInput}
              className="text-xs font-semibold text-stone-600 underline"
            >
              번호 다시 입력하기
            </button>
          </div>
        </div>
      )}

      {verificationError && (
        <p className="mt-3 text-center text-sm text-red-500">{verificationError}</p>
      )}

      {submitError && (
        <p className="mt-3 text-center text-sm text-red-500">{submitError}</p>
      )}

      {!hideSubmitButton && (
        <button
          onClick={() => {
            if (isSubmitting) return;
            if (!isVerified) {
              setVerificationError('본인 인증을 완료해 주세요.');
              return;
            }
            onSubmit();
          }}
          className={`mt-5 flex w-full items-center justify-center rounded-lg py-4 font-medium transition ${
            isSubmitting || !isVerified
              ? 'bg-stone-200 text-stone-400'
              : 'bg-stone-800 text-white active:bg-stone-900'
          }`}
        >
          {isSubmitting ? '처리 중...' : '받기'}
        </button>
      )}
    </div>
  );
};

export default PhoneVerificationSection;
